import React, { createContext, useContext, useEffect, useMemo } from "react";
import { LocaleNamespace } from "models/Locale";

export type AccountTranslationKey = string;
export type AccountLocale = "en" | "nl" | "fr" | "de" | "es" | "pl" | "ro" | "it";

export const accountLocaleOptions: ReadonlyArray<{
    value: AccountLocale;
    nativeLabel: string;
    labelKey: AccountTranslationKey;
}> = [
    { value: "en", nativeLabel: "English", labelKey: "languageEnglish" },
    { value: "nl", nativeLabel: "Nederlands", labelKey: "languageDutch" },
    { value: "fr", nativeLabel: "Français", labelKey: "languageFrench" },
    { value: "de", nativeLabel: "Deutsch", labelKey: "languageGerman" },
    { value: "es", nativeLabel: "Español", labelKey: "languageSpanish" },
    { value: "pl", nativeLabel: "Polski", labelKey: "languagePolish" },
    { value: "ro", nativeLabel: "Română", labelKey: "languageRomanian" },
    { value: "it", nativeLabel: "Italiano", labelKey: "languageItalian" },
];

const supportedAccountLocales = new Set<AccountLocale>(accountLocaleOptions.map(option => option.value));

export const normalizeAccountLocale = (locale?: string | null): AccountLocale => {
    const language = locale?.trim().toLowerCase().split(/[-_]/)[0] as AccountLocale | undefined;
    return language && supportedAccountLocales.has(language) ? language : "en";
};

export const detectBrowserAccountLocale = (languages?: readonly string[]): AccountLocale => {
    const browserLanguages = languages ?? (
        typeof navigator === "undefined"
            ? []
            : navigator.languages?.length > 0
                ? navigator.languages
                : [navigator.language]
    );
    for (const locale of browserLanguages) {
        const language = locale?.trim().toLowerCase().split(/[-_]/)[0] as AccountLocale | undefined;
        if (language && supportedAccountLocales.has(language)) {
            return language;
        }
    }
    return "en";
};

export const syncBrowserAccountLocale = async <T extends { locale?: string | null }>(
    value: T,
    persist: (localizedValue: T) => Promise<T>,
    languages?: readonly string[],
): Promise<T> => {
    const locale = detectBrowserAccountLocale(languages);
    if (normalizeAccountLocale(value.locale) === locale) {
        return value;
    }
    return persist({ ...value, locale } as T);
};

const localeToIntl: Record<AccountLocale, string> = {
    en: "en-GB",
    nl: "nl-NL",
    fr: "fr-FR",
    de: "de-DE",
    es: "es-ES",
    pl: "pl-PL",
    ro: "ro-RO",
    it: "it-IT",
};

type TranslationParams = Record<string, string | number>;

export const getAccountTranslation = (
    locale: string | null | undefined,
    translations: LocaleNamespace,
    intlLocale?: string,
) => {
    const normalizedLocale = normalizeAccountLocale(locale);
    const t = (key: AccountTranslationKey, params: TranslationParams = {}) => {
        const value = translations[key] ?? key;
        return Object.entries(params).reduce(
            (text, [name, replacement]) => text.replaceAll(`{{${name}}}`, String(replacement)),
            value,
        );
    };

    return {
        locale: normalizedLocale,
        intlLocale: intlLocale || localeToIntl[normalizedLocale],
        t,
    };
};

type AccountTranslation = ReturnType<typeof getAccountTranslation>;

const AccountTranslationContext = createContext<AccountTranslation>(
    getAccountTranslation("en", {}),
);

export const AccountTranslationProvider: React.FC<{
    locale?: string | null;
    intlLocale?: string;
    translations: LocaleNamespace;
    children: React.ReactNode;
}> = ({ locale, intlLocale, translations, children }) => {
    const translation = useMemo(
        () => getAccountTranslation(locale, translations, intlLocale),
        [intlLocale, locale, translations],
    );
    useEffect(() => {
        const previousLocale = document.documentElement.lang;
        document.documentElement.lang = translation.locale;
        return () => {
            document.documentElement.lang = previousLocale;
        };
    }, [translation.locale]);

    return (
        <AccountTranslationContext.Provider value={translation}>
            {children}
        </AccountTranslationContext.Provider>
    );
};

export const useAccountTranslation = () => useContext(AccountTranslationContext);
